import type { TenantMembership } from '@cdf/api-client-react';

import { tenantPath, tenantSlugFromPath } from '@/features/tenancy/urls';

export interface TenantChoice {
  id: string;
  name: string;
  entities: TenantMembership[];
}

export function selectedEntity(
  entities: readonly TenantMembership[],
  pathname: string
): TenantMembership | undefined {
  const slug = tenantSlugFromPath(pathname);
  return entities.find((entity) => entity.baseSlug === slug || entity.alias === slug);
}

export function defaultEntity(entities: readonly TenantMembership[]): TenantMembership | undefined {
  return entities.find((entity) => entity.status === 'active') ?? entities[0];
}

export function tenantChoices(entities: readonly TenantMembership[]): TenantChoice[] {
  const choices = new Map<string, TenantChoice>();
  for (const entity of entities) {
    const choice = choices.get(entity.tenantId) ?? {
      id: entity.tenantId,
      name: entity.tenantName,
      entities: []
    };
    choice.entities.push(entity);
    choices.set(entity.tenantId, choice);
  }
  return [...choices.values()];
}

function comparableName(name: string): string {
  return name.normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ').trim().replace(/\s+/g, ' ');
}

function sameNameOrRepeatedLetterTypo(first: string, second: string): boolean {
  const left = comparableName(first);
  const right = comparableName(second);
  if (left === right) return true;
  const [longer, shorter] = left.length > right.length ? [left, right] : [right, left];
  if (longer.length !== shorter.length + 1) return false;
  return [...longer].some((letter, index) =>
    index > 0 && letter === longer[index - 1] &&
    longer.slice(0, index) + longer.slice(index + 1) === shorter
  );
}

/** Avoid repeating a parent name when its only granted entity has the same display name. */
export function singleEntitySharesTenantName(
  entities: readonly TenantMembership[],
  selected: TenantMembership
): boolean {
  return entities.filter((entity) => entity.tenantId === selected.tenantId).length === 1 &&
    sameNameOrRepeatedLetterTypo(selected.tenantName, selected.name);
}

export function entityCacheIdentity(accountKey: string | null, pathname: string): string {
  return JSON.stringify([accountKey, tenantSlugFromPath(pathname)]);
}

export function entitySwitchPath(entity: TenantMembership): string {
  return tenantPath(entity.slug, entity.status === 'active' ? '' : 'tenant/entity');
}
